import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SplashProvider, useSplash } from './context/SplashContext';
import PrivateRoute from './components/auth/PrivateRoute';
import AppShell from './components/layout/AppShell';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import TwoFactorSetupPage from './pages/auth/TwoFactorSetupPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import HFLayout from './pages/hf/HFLayout';
import SAALayout from './pages/saa/SAALayout';
import TradingLayout from './pages/trading/TradingLayout';
import SaudiLayout from './pages/saudi/SaudiLayout';
import GccFlowsLayout from './pages/gcc/GccFlowsLayout';
import TechnicalLayout from './pages/signals/TechnicalLayout';
import ResearchOSLayout from './pages/researchos/ResearchOSLayout';
import AgentXPage from './pages/agentx/AgentXPage';
import './App.css';

// Brand-new visitor only: play the boot splash once ever (per browser), then reveal the
// login page underneath. NOT on refreshes — the localStorage flag makes it fire once.
// (Login→enter and tile-click play their own splashes via useSplash.)
function FirstVisitGate() {
  const { playSplash } = useSplash();
  React.useEffect(() => {
    try {
      if (!localStorage.getItem('mgv_seen')) {
        localStorage.setItem('mgv_seen', '1');
        playSplash(null, 4200);
      }
    } catch (e) { /* localStorage blocked → just skip the splash */ }
  }, [playSplash]);
  return null;
}

// Auth-only guard for /change-password: requires a session but does NOT apply the
// must_change_password redirect (that would loop, since this IS the change page).
function AuthOnlyRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <SplashProvider>
        <FirstVisitGate />
        <BrowserRouter>
          <Routes>
            <Route path="/login"     element={<LoginPage />} />
            <Route path="/register"  element={<RegisterPage />} />

            {/* 2FA setup — validated by setup_token in sessionStorage, not auth token */}
            <Route path="/setup-2fa" element={<TwoFactorSetupPage />} />

            {/* Forced first-login password change. Auth-only (NOT behind PrivateRoute's
                must_change redirect, so must-change users can actually reach it). */}
            <Route path="/change-password" element={<AuthOnlyRoute><ChangePasswordPage /></AuthOnlyRoute>} />

            <Route
              path="/*"
              element={
                <PrivateRoute>
                  <AppShell>
                    <Routes>
                      <Route index element={<DashboardPage />} />
                      <Route path="hf/*"      element={<HFLayout />} />
                      <Route path="saa/*"     element={<SAALayout />} />
                      <Route path="trading/*" element={<TradingLayout />} />
                      <Route path="saudi/*"   element={<SaudiLayout />} />
                      <Route path="gcc/*"     element={<GccFlowsLayout />} />
                      <Route path="signals/*" element={<TechnicalLayout />} />
                      <Route path="researchos/*" element={<ResearchOSLayout />} />
                      <Route path="agentx"   element={<AgentXPage />} />
                    </Routes>
                  </AppShell>
                </PrivateRoute>
              }
            />
          </Routes>
        </BrowserRouter>
      </SplashProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
